import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, exhaustMap, catchError, withLatestFrom } from 'rxjs/operators';
import { EmpresaService, CreateEmpresaRequest, UpdateEmpresaRequest } from '@hta/auth';
import { ContribuyenteWizardActions } from './contribuyente-wizard.actions';
import { selectWizardDataForSubmit } from './contribuyente-wizard.selectors';

/**
 * Extracts the code from a display value like "[99] Por definir" → "99"
 * or "[G03] Gastos en general" → "G03"
 */
function extractCode(displayValue: string | undefined | null): string | undefined {
    if (!displayValue) return undefined;
    const match = displayValue.match(/^\[([^\]]+)\]/);
    return match ? match[1] : displayValue;
}

@Injectable()
export class ContribuyenteWizardEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly empresaService = inject(EmpresaService);

    /**
     * Effect to submit the wizard data to the backend
     */
    submitWizard$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContribuyenteWizardActions.submitWizard),
            withLatestFrom(this.store.select(selectWizardDataForSubmit)),
            exhaustMap(([action, wizardData]) => {
                // Extract regimenFiscalFactura - could be string or object
                let regimenFiscalFacturaValue: string | undefined;
                const rawRegimen = wizardData.facturacionPago.regimenFiscalFactura;
                
                if (rawRegimen != null && typeof rawRegimen === 'object' && 'codigo' in (rawRegimen as object)) {
                    // It's an object, extract the codigo
                    regimenFiscalFacturaValue = (rawRegimen as { codigo: string }).codigo;
                } else if (rawRegimen && typeof rawRegimen === 'string' && rawRegimen.trim() !== '') {
                    regimenFiscalFacturaValue = rawRegimen;
                } else {
                    regimenFiscalFacturaValue = undefined;
                }

                // Build the unified request
                const request: CreateEmpresaRequest = {
                    // Step 1: Info Contribuyente
                    rfc: wizardData.infoContribuyente.rfc,
                    razonSocial: wizardData.infoContribuyente.razonSocial,
                    regimenCapital: wizardData.infoContribuyente.regimenCapital,
                    logoUrl: wizardData.infoContribuyente.logoUrl,
                    direccionFiscal: wizardData.infoContribuyente.direccionFiscal,
                    usarDatosUsuarioPropietario: wizardData.infoContribuyente.usarDatosUsuarioPropietario,
                    nombreContacto: wizardData.infoContribuyente.nombreContacto,
                    emailContacto: wizardData.infoContribuyente.emailContacto,
                    telefonoContacto: wizardData.infoContribuyente.telefonoContacto,
                    regimenesFiscales: wizardData.infoContribuyente.regimenesFiscales.length > 0
                        ? wizardData.infoContribuyente.regimenesFiscales
                        : undefined,

                    // Step 2: Certificados (passed from component after file conversion)
                    csd: action.csdInfo,
                    fiel: action.fielInfo,

                    // Step 3: Facturacion
                    facturarA: wizardData.facturacionPago.facturarA,
                    rfcFactura: wizardData.facturacionPago.rfcFactura || undefined,
                    razonSocialFactura: wizardData.facturacionPago.razonSocialFactura || undefined,
                    regimenFiscalFactura: regimenFiscalFacturaValue,
                    formaPago: extractCode(wizardData.facturacionPago.formaPago),
                    usoCfdi: extractCode(wizardData.facturacionPago.usoCfdi),
                    direccionFactura: wizardData.facturacionPago.direccionFactura
                };

                // Call the service
                return of(request).pipe(
                    exhaustMap(async (req) => {
                        // Crear la empresa/contribuyente
                        // Nota: La sesión/cookie ya debe estar establecida al crear o seleccionar el corporativo
                        const empresa = await this.empresaService.create(req);
                        return ContribuyenteWizardActions.submitWizardSuccess({ empresa });
                    }),
                    catchError((error) => {
                        const message = error?.error?.error || error?.message || 'Error al crear el contribuyente';
                        return of(ContribuyenteWizardActions.submitWizardFailure({ error: message }));
                    })
                );
            })
        )
    );

    /**
     * Effect to update an existing empresa (edit mode)
     */
    updateWizard$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContribuyenteWizardActions.updateWizard),
            withLatestFrom(this.store.select(selectWizardDataForSubmit)),
            exhaustMap(([action, wizardData]) => {
                // Extract regimenFiscalFactura - could be string or object
                let regimenFiscalFacturaValue: string | undefined;
                const rawRegimen = wizardData.facturacionPago.regimenFiscalFactura;
                
                if (rawRegimen != null && typeof rawRegimen === 'object' && 'codigo' in (rawRegimen as object)) {
                    regimenFiscalFacturaValue = (rawRegimen as { codigo: string }).codigo;
                } else if (rawRegimen && typeof rawRegimen === 'string' && rawRegimen.trim() !== '') {
                    regimenFiscalFacturaValue = rawRegimen;
                } else {
                    regimenFiscalFacturaValue = undefined;
                }

                // Build the update request
                const request: UpdateEmpresaRequest = {
                    // Step 1: Info Contribuyente
                    rfc: wizardData.infoContribuyente.rfc,
                    razonSocial: wizardData.infoContribuyente.razonSocial,
                    regimenCapital: wizardData.infoContribuyente.regimenCapital,
                    logoUrl: wizardData.infoContribuyente.logoUrl,
                    direccionFiscal: wizardData.infoContribuyente.direccionFiscal,
                    usarDatosUsuarioPropietario: wizardData.infoContribuyente.usarDatosUsuarioPropietario,
                    nombreContacto: wizardData.infoContribuyente.nombreContacto,
                    emailContacto: wizardData.infoContribuyente.emailContacto,
                    telefonoContacto: wizardData.infoContribuyente.telefonoContacto,
                    regimenesFiscales: wizardData.infoContribuyente.regimenesFiscales.length > 0
                        ? wizardData.infoContribuyente.regimenesFiscales
                        : undefined,

                    // Step 2: Certificados (only if new files are provided)
                    csd: action.csdInfo,
                    fiel: action.fielInfo,

                    // Step 3: Facturacion
                    facturarA: wizardData.facturacionPago.facturarA,
                    rfcFactura: wizardData.facturacionPago.rfcFactura || undefined,
                    razonSocialFactura: wizardData.facturacionPago.razonSocialFactura || undefined,
                    regimenFiscalFactura: regimenFiscalFacturaValue,
                    formaPago: extractCode(wizardData.facturacionPago.formaPago),
                    usoCfdi: extractCode(wizardData.facturacionPago.usoCfdi),
                    direccionFactura: wizardData.facturacionPago.direccionFactura
                };

                // Call the service
                return of({ request, empresaId: action.empresaId }).pipe(
                    exhaustMap(async ({ request: req, empresaId }) => {
                        const empresa = await this.empresaService.update(empresaId, req);
                        return ContribuyenteWizardActions.updateWizardSuccess({ empresa });
                    }),
                    catchError((error) => {
                        const message = error?.error?.error || error?.message || 'Error al actualizar el contribuyente';
                        return of(ContribuyenteWizardActions.updateWizardFailure({ error: message }));
                    })
                );
            })
        )
    );
}
